import React, { useState, useEffect, useContext } from "react";
import "../styles/styles.css";
import { icons } from "../../../constants";
import { apiRoutes } from "../../../utils/apiRoutes";
import { fetchData, createData } from "../../../utils/methods";
import { useAlert } from "../../../utils/AlertContext";
import { useNavigate } from "react-router-dom";
import { TranslationsContext } from "../../../utils/TranslationsContext";
import Loader from "../../Loader/Loader";
import AccreditationInput from "../../AccreditationInput/AccreditationInput";
import GroupModalTable from "./GroupModalTable";
import AccreditationButton from "../../AccreditationButton/AccreditationButton";

const AddUsersToEventGroup = ({
  onCloseClick,
  eventId,
  activeGroup,
  usersOnEvent,
  handleUpdateData,
}) => {
  console.log("usersOnEvent", usersOnEvent);
  const { t } = useContext(TranslationsContext);
  const { showAlert } = useAlert();
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [dataLength, setDataLength] = useState(0);

  const [selectedGroup, setSelectedGroup] = useState(undefined);
  const [originalMembers, setOriginalMembers] = useState([]);
  const [addedUsers, setAddedUsers] = useState([]);
  const [removedUsers, setRemovedUsers] = useState([]);
  const [isGroupLocked, setIsGroupLocked] = useState(!!activeGroup);
  const [visibleUsers, setVisibleUsers] = useState([]);

  // utils/flattenGroups.js
  function flattenGroups(list) {
    const result = [];
    const seen = new Set();

    const pickNaziv = (node) =>
      node?.naziv ?? node?.name ?? node?.title ?? node?.label ?? "";

    const pickId = (node) =>
      node?.id ?? node?._id ?? node?.groupId ?? node?.uuid ?? null;

    const dfs = (nodes) => {
      if (!Array.isArray(nodes)) return;
      for (const node of nodes) {
        const id = pickId(node);
        const naziv = pickNaziv(node);
        const korisnici = Array.isArray(node?.korisnici) ? node.korisnici : [];

        if (id != null && !seen.has(id)) {
          seen.add(id);
          result.push({ id, naziv, korisnici });
        }

        if (Array.isArray(node?.children) && node.children.length) {
          dfs(node.children);
        }
      }
    };

    dfs(list);
    return result;
  }

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setIsLoadingGroups(true);
        const route = apiRoutes.getEventGroups.replace(":eventId", eventId);
        const result = await fetchData(route, showAlert, navigate, t);
        const resultAllGroups = await fetchData(
          apiRoutes.getGroups,
          showAlert,
          navigate,
          t,
        );
        if (!result) return;

        console.log("resultAllgroups", resultAllGroups);

        const { data, headers } = result;
        setDataLength(Number(headers.get("x-total-count") || 0));
        console.log("data", data);

        const raw = Array.isArray(data) ? data : data?.data || [];
        const flat = flattenGroups(raw);

        setGroups(flat); // [{ id, naziv }, ...]
      } catch (err) {
        console.error("Error fetching groups:", err);
      } finally {
        setIsLoadingGroups(false);
      }
    };
    fetchGroups();
  }, []);

  console.log("grupe", groups);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setIsLoadingData(true);
        const result = await fetchData(
          apiRoutes.getUsers,
          showAlert,
          navigate,
          t,
        );
        if (!result) return;

        const { data, headers } = result;
        setDataLength(Number(headers.get("x-total-count") || 0));
        setAllUsers(data);
        console.log("allUsers", allUsers);
      } catch (err) {
        console.error("Error fetching users:", err);
      } finally {
        setIsLoadingData(false);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (!activeGroup || allUsers.length === 0 || groups.length === 0) return;
    const fullGroup = groups.find((g) => g.id === activeGroup.id);
    if (!fullGroup) return;

    const fullUsers = (fullGroup.korisnici || [])
      .map((rel) => allUsers.find((u) => u.id === rel.korisnikId))
      .filter(Boolean);

    setSelectedGroup({
      ...fullGroup,
      korisnici: fullUsers,
      naziv: fullGroup.naziv,
    });
    setOriginalMembers(fullUsers);
    setAddedUsers([]);
    setRemovedUsers([]);
  }, [activeGroup, allUsers, groups]);

  const onSelectGroup = (selected_id) => {
    const group = groups.find((g) => g.id === selected_id);
    if (!group) return;

    const fullUsers = (group.korisnici || [])
      .map((rel) => allUsers.find((u) => u.id === rel.korisnikId))
      .filter(Boolean);

    setSelectedGroup({ ...group, korisnici: fullUsers, naziv: group.naziv });
    setOriginalMembers(fullUsers);
    setAddedUsers([]);
    setRemovedUsers([]);
  };

  const handleCheckboxChange = (value, memberId) => {
    if (!selectedGroup) return;
    if (!isGroupLocked) setIsGroupLocked(true);

    setSelectedGroup((prevGroup) => {
      const currentMembers = prevGroup.korisnici || [];
      const isAlreadyMember = currentMembers.some((u) => u.id === memberId);
      const member = allUsers.find((u) => u.id === memberId);
      if (!member) return prevGroup;

      let updatedMembers;
      let newAdded = [...addedUsers];
      let newRemoved = [...removedUsers];

      if (value) {
        if (!isAlreadyMember) {
          updatedMembers = [...currentMembers, member];
          if (originalMembers.some((u) => u.id === memberId)) {
            newRemoved = newRemoved.filter((u) => u.id !== memberId);
          } else if (!newAdded.some((u) => u.id === memberId)) {
            newAdded.push(member);
          }
        } else return prevGroup;
      } else {
        updatedMembers = currentMembers.filter((u) => u.id !== memberId);
        if (originalMembers.some((u) => u.id === memberId)) {
          if (!newRemoved.some((u) => u.id === memberId))
            newRemoved.push(member);
        } else {
          newAdded = newAdded.filter((u) => u.id !== memberId);
        }
      }

      setAddedUsers(newAdded);
      setRemovedUsers(newRemoved);

      return { ...prevGroup, korisnici: updatedMembers };
    });
  };

  const sendData = async ({ add, data }) => {
    const route = add
      ? apiRoutes.addEventGroupUser
      : apiRoutes.deleteEventGroupUser;
    try {
      await createData(route, data, showAlert, navigate, t);
    } catch (err) {
      console.error("API poziv nije uspeo:", err.message);
    }
  };

  useEffect(() => {
    // Handle Enter key to trigger primary action
    const handleEnterKey = (event) => {
      if (
        event.key === "Enter" &&
        !event.shiftKey &&
        !event.ctrlKey &&
        !event.metaKey
      ) {
        // Don't trigger if user is typing in a textarea or input
        const target = event.target;
        if (
          target.tagName === "TEXTAREA" ||
          (target.tagName === "INPUT" && target.type !== "submit")
        ) {
          return;
        }
        event.preventDefault();
        handleSaveClick();
      }
    };

    document.addEventListener("keydown", handleEnterKey);

    return () => {
      document.removeEventListener("keydown", handleEnterKey);
    };
  }, [selectedGroup, addedUsers, removedUsers]);

  const handleSaveClick = async () => {
    if (!selectedGroup) return;
    setIsLoading(true);
    try {
      for (const userId of addedUsers.map((u) => u.id)) {
        await sendData({
          add: true,
          data: {
            dogadjajId: eventId,
            dogadjajGrupaId: selectedGroup.id,
            korisnikId: userId,
          },
        });
      }
      for (const userId of removedUsers.map((u) => u.id)) {
        await sendData({
          add: false,
          data: {
            dogadjajGrupaId: selectedGroup.id,
            korisnikId: userId,
          },
        });
      }

      showAlert(t("SUCCESSFULLY_SAVED"), "success");
      if (handleUpdateData) {
        handleUpdateData({
          addedUsers,
          removedUsers,
          groupId: selectedGroup.id,
        });
      }
      onCloseClick();
    } finally {
      setIsLoading(false);
    }
  };

  const areSameUsers = (a = [], b = []) => {
    const aIds = a
      .map((u) => u.id)
      .sort()
      .join(",");
    const bIds = b
      .map((u) => u.id)
      .sort()
      .join(",");
    return aIds === bIds;
  };

  useEffect(() => {
    if (activeGroup) return;
    if (!selectedGroup || !originalMembers) return;
    const current = selectedGroup.korisnici || [];
    setIsGroupLocked(!areSameUsers(current, originalMembers));
  }, [selectedGroup?.korisnici, originalMembers, activeGroup]);

  useEffect(() => {
    if (!selectedGroup || allUsers.length === 0) return;
    setVisibleUsers(() => {
      const selectedGroupUserIds = new Set(
        selectedGroup.korisnici?.map((u) => u.id),
      );
      return allUsers.filter((user) => {
        const isRemoved = removedUsers.some((u) => u.id === user.id);
        const isAdded = addedUsers.some((u) => u.id === user.id);
        const isOriginallyInEvent = usersOnEvent.some((u) => u.id === user.id);
        const isInUsersOnEvent = (isOriginallyInEvent && !isRemoved) || isAdded;
        const isInSelectedGroup = selectedGroupUserIds.has(user.id);
        return !isInUsersOnEvent || isInSelectedGroup;
      });
    });
  }, [selectedGroup?.id, allUsers, usersOnEvent, addedUsers, removedUsers]);

  if (isLoading) return <Loader />;

  return (
    <div className="flex-direction-column gap-24 justify-between add-users-to-event-group-modal">
      <div className="flex-direction-column width-100 gap-36">
        <div className="flex-direction-row justify-between pointer width-100">
          <p className="inria-sans-400 font-24 modal-title"></p>
          <img
            className="pointer"
            src={icons.close_gray}
            onClick={onCloseClick}
            alt="X"
          />
        </div>

        {!activeGroup || selectedGroup ? (
          <AccreditationInput
            type="dropdown"
            disabled={isGroupLocked}
            searchable={true}
            label={t("GROUP")}
            className="modal-input"
            selectedOption={selectedGroup?.naziv || ""}
            setSelectedOption={onSelectGroup}
            options={groups?.map((g) => ({ value: g.naziv, key: g.id }))}
          />
        ) : null}

        {selectedGroup ? (
          <GroupModalTable
            members={visibleUsers}
            selectedMembers={selectedGroup?.korisnici || []}
            onCheckboxChange={handleCheckboxChange}
            isLoadingData={isLoadingData}
          />
        ) : (
          <p
            className="font-16 inria-sans-400"
            style={{ color: "var(--text-black)" }}
          >
            {t("TO_ADD_USERS_YOU_MUST_FIRST_SELECT_THE_GROUP")}
          </p>
        )}
      </div>

      <AccreditationButton
        onClick={handleSaveClick}
        type="save"
        title={t("BTN_SAVE")}
        className="modal-btn"
      />
    </div>
  );
};

export default AddUsersToEventGroup;
